import {
  DoorSlidingOutlined,
  Lock,
  LockOpen,
  MapOutlined,
  RouteOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import { IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  HandoverIcon,
  InfiniteIcon,
  ShapesIcon,
  TrainIcon,
  TruckIcon,
  useMinimalsTheme,
  VesselFrontIcon,
  YardIcon,
} from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { BerthViewStore } from '../../stores/nodes/berth.view-store'
import { FlexibleBlockViewStore } from '../../stores/nodes/flexible-block.view-store'
import { LayoutObjectViewStore } from '../../stores/nodes/layout-object.view-store'
import { RailTrackViewStore } from '../../stores/nodes/rail-track.view-store'
import { RoadNetworkViewStore } from '../../stores/nodes/road-network.view-store'
import { YardBlockViewStore } from '../../stores/nodes/yard-block.view-store'
import { YardViewStore } from '../../stores/yard.view-store'
import { BackgroundModeSelector } from '../atoms/BackgroundModeSelector'

interface Props {
  yardBlockViewStore: YardBlockViewStore
  flexibleBlockViewStore: FlexibleBlockViewStore
  layoutObjectViewStore: LayoutObjectViewStore
  berthViewStore: BerthViewStore
  railTrackViewStore: RailTrackViewStore
  roadNetworkViewStore: RoadNetworkViewStore
  yardViewStore: YardViewStore
}
export const YardSetupHeader = observer(
  ({
    yardViewStore,
    yardBlockViewStore,
    flexibleBlockViewStore,
    layoutObjectViewStore,
    berthViewStore,
    railTrackViewStore,
    roadNetworkViewStore,
  }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    return (
      <Stack
        sx={{
          position: 'absolute',
          top: theme.customSpacing.l,
          left: theme.customSpacing.l,
          zIndex: theme.zIndex.appBar,
          gap: theme.customSpacing.m,
        }}
      >
        <Typography variant='h2'>{t('yardSetup', 'Yard setup')}</Typography>
        <Stack sx={{ display: 'flex ', flexDirection: 'row', gap: theme.customSpacing.m }}>
          <Paper elevation={4}>
            <Tooltip title={t('addBackground', 'Add background')}>
              <IconButton
                onClick={() => layoutObjectViewStore.openBackgroundLayoutObjectFormDialog()}
              >
                <MapOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                yardViewStore.isBackgroundEditBlocked
                  ? t('unlockBackground', 'Unlock background')
                  : t('lockBackground', 'Lock background')
              }
            >
              <IconButton
                onClick={() =>
                  yardViewStore.setIsBackgroundEditBlocked(!yardViewStore.isBackgroundEditBlocked)
                }
              >
                {yardViewStore.isBackgroundEditBlocked ? <Lock /> : <LockOpen />}
              </IconButton>
            </Tooltip>
            <BackgroundModeSelector yardViewStore={yardViewStore} />
            <Tooltip title={t('toggleNodeHeaders', 'Toggle node headers')}>
              <IconButton
                onClick={() => yardViewStore.setShowNodeHeaders(!yardViewStore.showNodeHeaders)}
              >
                {yardViewStore.showNodeHeaders ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
          </Paper>
          <Paper elevation={4}>
            <Tooltip title={t('createBerth', 'Create berth')}>
              <IconButton onClick={() => berthViewStore.openFormDialog()}>
                <VesselFrontIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('createRailTrack', 'Create rail track')}>
              <IconButton onClick={() => railTrackViewStore.openFormDialog()}>
                <TrainIcon />
              </IconButton>
            </Tooltip>
          </Paper>
          <Paper elevation={4}>
            <Tooltip title={t('createGate', 'Create gate')}>
              <IconButton onClick={() => layoutObjectViewStore.openGateFormDialog()}>
                <DoorSlidingOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                roadNetworkViewStore.isDrawingMode
                  ? t('exitRoadManagement', 'Exit road management')
                  : t('manageRoad', 'Manage road')
              }
            >
              <IconButton
                onClick={() => roadNetworkViewStore.toggleDrawingMode()}
                sx={{
                  bgcolor: roadNetworkViewStore.isDrawingMode ? 'primary.main' : undefined,
                  color: roadNetworkViewStore.isDrawingMode ? 'white' : undefined,
                  '&:hover': roadNetworkViewStore.isDrawingMode
                    ? { bgcolor: 'primary.dark' }
                    : undefined,
                }}
              >
                <RouteOutlined />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('createHoldingPoint', 'Create holding point')}>
              <IconButton onClick={() => layoutObjectViewStore.openHoldingPointFormDialog()}>
                <TruckIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('createHandoverArea', 'Create handover area')}>
              <IconButton onClick={() => layoutObjectViewStore.openHandoverAreaFormDialog()}>
                <HandoverIcon />
              </IconButton>
            </Tooltip>
          </Paper>
          <Paper elevation={4} sx={{ width: 'fit-content' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Tooltip title={t('createBlock', 'Create block')}>
                <IconButton onClick={() => yardBlockViewStore.openFormDialog()}>
                  <YardIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('createFlexibleBlock', 'Create flexible block')}>
                <IconButton onClick={() => flexibleBlockViewStore.openFormDialog()}>
                  <InfiniteIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('createLayoutObject', 'Create layout object')}>
                <IconButton
                  onClick={() => layoutObjectViewStore.openStandardLayoutObjectFormDialog()}
                >
                  <ShapesIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    )
  },
)
